// Lib
import { map, negate, first, flatMap } from 'lodash/fp';

// Services
import { fetchBoard, fetchVisibleDescendantBoards } from '../board/boardService';

// Actions
import { createMultipleElementsSync } from '../actions/elementActions';
import { deleteElement } from '../../../common/elements/elementActions';
import { increaseElementCount } from '../../user/elementCount/elementCountActions';
import { setSelectedElements } from '../selection/selectionActions';

// Selectors
import { getElements } from '../selectors/elementsSelector';
import { elementGraphSelector } from '../selectors/elementGraphSelector';
import { getClientIdSelector, getClientTickSelector } from '../../user/clientDataSelector';

// Util
import http from '../../utils/services/http';
import { asObject, getMany } from '../../../common/utils/immutableHelper';
import createElementId from '../../../common/uid/createElementId';
import { getAllChildIds, getDescendantIds } from '../../../common/dataStructures/graphUtils';
import { getBoardContentFromAlias } from '../../../common/alias/aliasUtils';
import { getElement } from '../../../common/elements/utils/elementTraversalUtils';
import { createDuplicateDefs } from '../../../common/elements/elementDuplicateUtil';
import { getNewTransactionId } from '../../utils/undoRedo/undoRedoTransactionManager';
import { getTokenForElementIdThunk } from '../../utils/permissions/permissionsActions';
import { getElementId, getLinkedElementId, getPhysicalId } from '../../../common/elements/utils/elementPropertyUtils';
import {
    isAlias,
    isBoard,
    isColumn,
    isCommentThread,
    isTaskLike,
} from '../../../common/elements/utils/elementTypeUtils';
import delay from '../../../common/utils/lib/delay';

// Constants
import { METHODS } from '../../../common/utils/http/httpConstants';
import { BOARD_TYPE } from '../../../common/boards/boardConstants';
import {
    ELEMENT_INDIVIDUAL_DUPLICATION,
    ELEMENT_DUPLICATION_PROCESSING,
    ELEMENT_DUPLICATION_COMPLETE,
} from './elementDuplicateConstants';

/**
 * This is used along with the duplicationReducer to keep track of elements that need a
 * "keep in-sync with original" message.
 */
const recordIndividualElementDuplication = (originalElementId, duplicateElementId) => ({
    type: ELEMENT_INDIVIDUAL_DUPLICATION,
    originalElementId,
    duplicateElementId,
});

export const setDuplicateLoading = (elementId) => ({
    type: ELEMENT_DUPLICATION_PROCESSING,
    elementId,
    loading: true,
});

export const setDuplicateLoadingComplete = (elementId) => ({
    type: ELEMENT_DUPLICATION_COMPLETE,
    elementId,
    loading: false,
});

const getCreateDuplicateIdFn = (state) => (duplicationIndex) => {
    const clientId = getClientIdSelector(state);
    const clientTick = getClientTickSelector(state) + duplicationIndex + 1;
    return createElementId(clientId, clientTick);
};

export const duplicateDescendantsIntoContainerElement =
    ({
        newElementId,
        oldElementId,
        location,
        elementType,
        transactionId,
        deleteOnError = true,
        showLoadingState = true,
    }) =>
    async (dispatch) => {
        const tokens = await dispatch(getTokenForElementIdThunk({ elementId: oldElementId }));

        if (showLoadingState) dispatch(setDuplicateLoading(newElementId));

        try {
            const {
                data: { elementCount },
            } = await http({
                url: `elements/${oldElementId}/duplicate`,
                method: METHODS.POST,
                data: {
                    element: JSON.stringify({
                        id: newElementId,
                        location,
                    }),
                    tokens,
                },
                timeout: 20000,
            });

            dispatch(increaseElementCount(elementCount, transactionId));

            const elementCountSum = Object.values(elementCount).reduce(
                (previousValue, currentValue) => previousValue + currentValue,
                0,
            );

            /**
        Adding a delay here because the scenario that we have to handle is that when duplicating large boards, there is an API call to fetch the
        duplicate elements of a board so in the use case that a user duplicates a large board and immediately clicks on the duplicating board
        we are also doing a API call the fetch the new board of the elements. So, we need to add an artificial delay on between the duplicating and
        fetching so that mongodb gets time to do the READ after WRITE and the user knows that the board is still duplicating.
        */

            await delay(elementCountSum * 0.25);

            const response = await dispatch(
                fetchBoard({
                    boardId: newElementId,
                    force: true,
                    excludeSelf: true,
                    loadAncestors: false,
                }),
            );

            dispatch(fetchVisibleDescendantBoards(newElementId));

            dispatch(setDuplicateLoadingComplete(newElementId));

            return response;
        } catch (err) {
            if (!err.response) return console.error(err);

            const { data } = err.response;

            dispatch(setDuplicateLoadingComplete(newElementId));

            if (deleteOnError) {
                dispatch(
                    deleteElement({
                        id: newElementId,
                        location,
                        elementType,
                        transactionId,
                    }),
                );
            }

            // TODO: show an error modal here
            alert(data.error && data.error.message); // eslint-disable-line no-alert
        }
    };

const toElement = (state) => (duplication) => {
    const copyElement = state.getIn(['elements', duplication.id]);
    if (!copyElement) return null;
    return { ...copyElement.toJS(), location: duplication.location };
};

/**
 * Duplicates visible elements within a single batched action and then
 * duplicates children of boards via server requests.
 */
const duplicateElementsBatched = (duplicateDefs, transactionId) => (dispatch, getState) => {
    dispatch(
        createMultipleElementsSync({
            // Need to map the _id property to "id"
            elements: duplicateDefs.map((def) => ({ ...def, id: def._id })),
            transactionId,
        }),
    );

    const updatedState = getState();
    const elementMap = getElements(updatedState);

    return duplicateDefs.map((elementDef) => {
        const oldElementId = elementDef.id;
        const newElementId = elementDef._id;

        if (isBoard(elementDef.elementType)) {
            dispatch(setDuplicateLoading(newElementId));
        }
        // don't continue if element creation wasn't successful
        const createdElement = getElement(elementMap, newElementId);

        if (!createdElement) return null;

        // We only do a server side duplication of board elements
        if (!isBoard(elementDef.elementType)) return createdElement;

        return dispatch(
            duplicateDescendantsIntoContainerElement({
                newElementId,
                oldElementId,
                location: elementDef.location,
                elementType: elementDef.elementType,
                transactionId,
            }),
        );
    });
};

const prepareAliasForDuplicationAsBoard = (elementMap, shouldConvertAliasToBoard) => (element) => {
    if (!shouldConvertAliasToBoard || !isAlias(element)) return element;

    const linkedBoardId = getLinkedElementId(element);
    const linkedBoard = getElement(elementMap, linkedBoardId);

    return {
        ...element,
        id: getPhysicalId(element),
        elementType: BOARD_TYPE,
        content: getBoardContentFromAlias(element, linkedBoard),
    };
};

/**
 * Gets all the children that will be duplicated on the client side.
 */
const getElementDescendantsToDuplicate = (state, { elementsToDuplicate }) => {
    // First get the container elements that have visible children
    const columnElements = elementsToDuplicate.filter(isColumn);

    const columnElementIds = columnElements.map(getElementId);

    const elements = getElements(state);
    const elementGraph = elementGraphSelector(state);

    const columnChildIds = getAllChildIds({ graph: elementGraph, parentIds: columnElementIds });
    const columnChildren = getMany(columnChildIds, elements);

    // Get all their children, then get all their descendants if they're not a board
    const columnChildrenToDescend = asObject(columnChildren.filter(isTaskLike).valueSeq());
    const duplicatesToDescend = elementsToDuplicate.filter(isTaskLike);

    const idsToDescend = [...columnChildrenToDescend.map(getElementId), ...duplicatesToDescend.map(getElementId)];

    const descendantIds = flatMap((parentId) => getDescendantIds({ graph: elementGraph, parentId }), idsToDescend);

    const idsToDuplicate = [...columnChildIds, ...descendantIds];

    const descendantsToDuplicate = getMany(idsToDuplicate, elements);

    return asObject(descendantsToDuplicate.valueSeq());
};

export const duplicateMultipleElements =
    ({ moves, duplications, shouldSelect, shouldConvertAliasToBoard, transactionId = getNewTransactionId() }) =>
    async (dispatch, getState) => {
        const state = getState();

        // Create elements object from duplication which is a snapshot of the original
        // object with updated location
        let elements = duplications || moves.map(toElement(state));

        // Here we want to add to the list of elements the contents of columns, as well as the deep contents of
        // task lists
        elements = [...elements, ...getElementDescendantsToDuplicate(state, { elementsToDuplicate: elements })];

        const elementMap = getElements(state);

        const resolvedElements = elements
            .filter(negate(isCommentThread))
            .map(prepareAliasForDuplicationAsBoard(elementMap, shouldConvertAliasToBoard));

        const createDuplicateIdFn = getCreateDuplicateIdFn(state);
        const duplicateDefs = createDuplicateDefs({ elements: resolvedElements, createDuplicateIdFn, transactionId });

        const duplicationPromise = dispatch(duplicateElementsBatched(duplicateDefs, transactionId));

        if (shouldSelect) dispatch(setSelectedElements({ ids: map('_id', duplicateDefs), transactionId }));

        const results = await Promise.all(duplicationPromise);

        if (results.length !== 1 || elements.length !== 1) return results;

        const originalElementId = getElementId(first(elements));
        const duplicateElementId = getElementId(first(duplicateDefs));

        dispatch(recordIndividualElementDuplication(originalElementId, duplicateElementId));

        return results;
    };
